import {IconDeviceGamepad2, IconFlame, IconMenu2, IconTicket, IconTrophy} from "@tabler/icons-react";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {useAuthStore} from "@/store/useAuth.store";
import MobileSidebar from "../../FloatingMenu/MobileSidebar";
import styles from "./MobileBottomDock.module.css";

/**
 * @typedef {"slots" | "lottery" | "feed" | "sports" | "menu"} DockItemId
 */

/** Route prefix matching for active detection */
const ROUTE_MAP = {
  slots: ["/casino", "/games"],
  lottery: ["/lottery", "/lotoven"],
  feed: ["/game-feed"],
  sports: ["/sports"],
};

const ICON_SIZE = 22;
const CENTER_ICON_SIZE = 26;

const MobileBottomDock = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const activeId = useMemo(() => {
    const path = location.pathname;
    for (const [id, prefixes] of Object.entries(ROUTE_MAP)) {
      if (prefixes.some(p => path === p || path.startsWith(`${p}/`))) {
        return id;
      }
    }
    return isSidebarOpen ? "menu" : null;
  }, [location.pathname, isSidebarOpen]);

  const requireAuth = useCallback(
    callback => {
      if (authStatus !== "authenticated") {
        useAuthStore.getState().handleAuthForm("login");
        handleShowAuthModal();
        return;
      }
      callback();
    },
    [authStatus, handleShowAuthModal]
  );

  const navigateTo = useCallback(
    path => {
      navigate(path);
      window.scrollTo({top: 0, behavior: "smooth"});
    },
    [navigate]
  );

  const handleSlots = useCallback(() => {
    requireAuth(() => navigateTo("/games/category/slots"));
  }, [requireAuth, navigateTo]);

  const handleLottery = useCallback(() => {
    requireAuth(() => navigateTo("/lottery"));
  }, [requireAuth, navigateTo]);

  const handleFeed = useCallback(() => {
    requireAuth(() => navigateTo("/game-feed"));
  }, [requireAuth, navigateTo]);

  const handleSports = useCallback(() => {
    requireAuth(() => navigateTo("/sports"));
  }, [requireAuth, navigateTo]);

  const handleMenu = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const sideItems = useMemo(
    () => [
      {
        id: "slots",
        label: t("floatingMenuSlots"),
        icon: IconDeviceGamepad2,
        onClick: handleSlots,
      },
      {
        id: "lottery",
        label: t("floatingMenuLottery"),
        icon: IconTicket,
        onClick: handleLottery,
      },
      {
        id: "sports",
        label: t("floatingMenuSports"),
        icon: IconTrophy,
        onClick: handleSports,
      },
      {
        id: "menu",
        label: t("floatingMenuMenu"),
        icon: IconMenu2,
        onClick: handleMenu,
      },
    ],
    [t, handleSlots, handleLottery, handleSports, handleMenu]
  );

  const feedActive = activeId === "feed";

  return (
    <>
      <MobileSidebar
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
      />

      <nav
        className={styles.dock}
        aria-label="Main navigation"
      >
        <div className={styles.dockRow}>
          {/* Left two items */}
          {sideItems.slice(0, 2).map(item => {
            const isActive = activeId === item.id;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`${styles.dockItem} ${isActive ? styles.dockItemActive : ""}`}
                onClick={item.onClick}
                aria-current={isActive ? "page" : undefined}
                aria-label={item.label}
              >
                <span className={styles.dockIndicator} />
                <span className={styles.dockIcon}>
                  <Icon
                    size={ICON_SIZE}
                    stroke={isActive ? 2.2 : 1.8}
                  />
                </span>
                <span className={styles.dockLabel}>{item.label}</span>
              </button>
            );
          })}

          {/* Center — Feed (prominent) */}
          <button
            type="button"
            className={`${styles.dockCenter} ${feedActive ? styles.dockCenterActive : ""}`}
            onClick={handleFeed}
            aria-current={feedActive ? "page" : undefined}
            aria-label={t("floatingMenuFeed")}
          >
            <span className={styles.centerRing} />
            <span className={styles.centerPulse} />
            <span className={styles.centerBtn}>
              <IconFlame
                size={CENTER_ICON_SIZE}
                stroke={2}
              />
            </span>
            <span className={styles.centerLabel}>{t("floatingMenuFeed")}</span>
          </button>

          {/* Right two items */}
          {sideItems.slice(2).map(item => {
            const isActive = activeId === item.id;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`${styles.dockItem} ${isActive ? styles.dockItemActive : ""}`}
                onClick={item.onClick}
                aria-current={isActive ? "page" : undefined}
                aria-label={item.label}
                aria-expanded={item.id === "menu" ? isSidebarOpen : undefined}
              >
                <span className={styles.dockIndicator} />
                <span className={styles.dockIcon}>
                  <Icon
                    size={ICON_SIZE}
                    stroke={isActive ? 2.2 : 1.8}
                  />
                </span>
                <span className={styles.dockLabel}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default MobileBottomDock;
